import React, {useEffect, useState} from 'react';
import {
  Stepper,
  Step,
  Container,
  Button,
  Box, LinearProgress,
} from '@material-ui/core';
import useAxios from 'axios-hooks';
import {Alert} from "@material-ui/lab";

import SideDrawer from "../../../Components/Advertisers/SideDrawer";
import ContentDiv from "../../../Components/Universal/ContentDiv";
import CustomText from "../../../Components/Universal/CustomText";
import {getFieldChangeSetter, WizardContext, WizardDefaultContext} from "./context";
import {useStyles, ColorlibConnector, ColorlibLabel} from './styles';
import WizardStep1 from "./components/WizardStep1";
import WizardStep2 from "./components/WizardStep2";
import WizardStep3 from "./components/WizardStep3";
import WizardStep4 from "./components/WizardStep4";
import WizardLocation from "./components/WizardLocation";
import {createCampaign, countAudience} from "../../../Api";

const NavIcon = ({ src }) => <img src={src} style={{ width: 80, height: 80, zIndex: 1000 }} alt="nav_icon" />

const steps = [
  { label: 'Choose Audience', icon: () =>  <NavIcon src="/icons/choose_audience.png" />},
  { label: 'Select Location', icon: () => <NavIcon src="/icons/choose_location.png" /> },
  { label: 'Campaign Details', icon: () => <NavIcon src="/icons/campaign_info.png" /> },
  { label: 'Question/Response Details', icon: () => <NavIcon src="/icons/visual_look.png" /> },
  { label: 'Review/Checkout', icon: () => <NavIcon src="/icons/review_checkout.png" /> },
]

export const CreateCampaign = (props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [backStepMin, setBackStepMin] = useState(0);
  const [formData, setFormData] = useState({...WizardDefaultContext.formData});
  const [errorMsg, setErrorMsg] = useState('');
  const [{error, loading, response}, createCampaginAction] = useAxios(createCampaign(), { manual: true });
  const [{loading: audienceLoading, data: audienceCountData}, countAudienceAction] = useAxios(countAudience(), { manual: true });
  const classes = useStyles();

  useEffect(() => {
    if (!loading && response && response.data) {
      if (response.data.error) {
        setErrorMsg(response.data.message);
        return;
      }

      window.location.href = '/advertiser/campaigns';
    } else if (error) {
      setErrorMsg('Unable to connect with server. Please try again later');
    }
  }, [response, loading, error]);

  const handleNextStep = (prevStep = false) => () => {
    window.scrollTo(0, 0);
    setActiveStep(activeStep + (prevStep ? -1 : 1));
  };

  const handleSubmit = () => {
    const resData = {...formData};
    if (resData.campaignImage) {
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(resData.campaignImage);
      fileReader.onload = () => {
        createCampaginAction({data: {
          ...resData,
          campaignImage: fileReader.result,
        }});
      };
    } else {
      createCampaginAction({data: {
        ...resData,
      }});
    }
  };

  const changeCallback = (name, targeting, value, finalData) => {
    if (name === 'targeting' || name === 'location' || targeting) {
      countAudienceAction({
        data: {
          targeting: finalData.targeting,
          location: finalData.location,
        },
      });
    }
  };

  const genericFieldChange = getFieldChangeSetter(formData, setFormData, changeCallback);

  useEffect(() => {
    const targetingStr = localStorage.getItem('home-targeting');
    if (targetingStr) {
      // moving forward
      setActiveStep(1);
      setBackStepMin(1);
      genericFieldChange('targeting', false)({target: { value: JSON.parse(targetingStr) }});
      localStorage.removeItem('home-targeting');
    }
  }, [genericFieldChange]);

  return (
    <SideDrawer>
      <WizardContext.Provider value={{formData, fieldChange: genericFieldChange }}>
        <ContentDiv>
          <Container component="main">
            <CustomText
              text='Create New Campaign'
              style='titleLarge'
              margin='90px 0 0 0'
            />
            <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
              {steps.map((step, idx) => (
                <Step key={`step_${idx}`}><ColorlibLabel StepIconComponent={step.icon}>{step.label}</ColorlibLabel></Step>
              ))}
            </Stepper>

            {errorMsg && errorMsg.length > 0 && (
              <Alert severity="error">{errorMsg}</Alert>
            )}

            <Box display='flex' justifyContent='center' mt={2}>
              <div style={{ display: 'flex', flex: 3, flexDirection: 'row', alignItems: 'center' }}>
                <form className={classes.stepForm}>
                  {activeStep === 0 && (
                    <WizardStep1 goToStep={setActiveStep} />
                  )}

                  {activeStep === 1 && (
                    <WizardLocation goToStep={setActiveStep} />
                  )}

                  {activeStep === 2 && (
                    <WizardStep2 goToStep={setActiveStep} />
                  )}

                  {activeStep === 3 && (
                    <WizardStep3 goToStep={setActiveStep} />
                  )}

                  {activeStep === 4 && (
                    <WizardStep4  goToStep={setActiveStep} />
                  )}
                </form>
                {activeStep !== 0 && (
                  <div style={{ flex: 1, alignItems: 'center' }}>
                    <CustomText
                      text="Total Count:"
                      style="totalCount"
                      align="center"
                    />
                    <CustomText style="totalNumber">{audienceCountData ? audienceCountData.total : 'All'}</CustomText>
                  </div>
                )}
              </div>
            </Box>

            <Box display="flex" justifyContent="center" mt={8}>
              {loading && <LinearProgress />}
              {(activeStep <= 1 || activeStep >= 4) && (
                <Button
                  onClick={() => { props.history.push('/') }}
                  style={{ marginRight: 10 }}
                >
                  Cancel
                </Button>
              )}
              {activeStep >= 1 && (
                <Button
                  className={classes.mainButton}
                  disabled={loading}
                  variant="contained"
                  color="primary"
                  onClick={handleNextStep(true)}
                  style={{ marginRight: 10 }}
                >
                  Back
                </Button>
              )}
              <Button
                className={classes.mainButton}
                disabled={loading}
                variant="contained"
                color="primary"
                onClick={activeStep >= 4 ? handleSubmit : handleNextStep()}
              >
                {activeStep >= 4 ? 'Checkout' : 'Next'}
              </Button>
            </Box>
          </Container>
        </ContentDiv>
      </WizardContext.Provider>
    </SideDrawer>
  );
};

export default CreateCampaign;
